import type { MyBusiness } from "@teamna/shared";
import type { ReactNode } from "react";
import {
  EmptyState,
  ErrorState,
  LockIcon,
  PageHeader,
  SkeletonList,
} from "../components/index.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";

/**
 * The frame of the full-width office pages (slice E: packages and rules, payments, team, settings).
 *
 * The same gate as `OwnerGate` (loading, failed, no business to manage), without its 760px reading
 * width: these pages hold tables and use the whole content column. The body is a function of the
 * business so a page never renders a row before it knows which business it is for.
 */
export function OfficePage({
  title,
  description,
  actions,
  back,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** A link to the list page this one opens from ("Tools" above a tool). */
  back?: { to: string; label: string };
  children: (business: MyBusiness) => ReactNode;
}) {
  const { business, managed, isLoading, error } = useActiveBusiness();

  let body: ReactNode;
  if (isLoading && !business) {
    body = <SkeletonList />;
  } else if (error && !business) {
    body = <ErrorState error={error} />;
  } else if (managed.length === 0 || !business) {
    body = (
      <EmptyState
        icon={<LockIcon size={30} />}
        title="No business to manage"
        message="This account does not run a business. Use the member side to see your own schedule."
      />
    );
  } else {
    body = children(business);
  }

  return (
    <div>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        {...(actions && business ? { actions } : {})}
        {...(back ? { back } : {})}
      />
      {body}
    </div>
  );
}

/** True for the two roles that run the business. Staff see the schedule and messages only. */
export function runsBusiness(business: MyBusiness): boolean {
  return business.staffRole === "owner" || business.staffRole === "admin";
}
